import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { PasswordInput } from '../components/ui/password-input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Badge } from '../components/ui/badge';
import { 
  Users, 
  UserPlus, 
  DotsThreeVertical, 
  PencilSimple, 
  Trash,
  Lock, 
  CircleNotch,
  MagnifyingGlass,
  ShieldCheck,
  ShieldWarning
} from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const roleColors = {
  super_admin: 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20',
  admin: 'bg-orange-500/10 text-orange-600 dark:text-orange-400 border-orange-500/20',
  manager: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20',
  staff: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20',
  auditor: 'bg-zinc-500/10 text-zinc-600 dark:text-zinc-400 border-zinc-500/20',
};

const roleLabels = {
  super_admin: 'Super Admin',
  admin: 'Admin',
  manager: 'Manager',
  staff: 'Staff',
  auditor: 'Auditor',
};

export default function UsersPage() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  
  const [newUser, setNewUser] = useState({
    email: '',
    first_name: '',
    last_name: '',
    password: '',
    role: 'staff'
  });

  const [editData, setEditData] = useState({
    first_name: '',
    last_name: '',
    role: '',
    is_active: true
  });

  const canManageUsers = currentUser?.permissions?.users?.includes('create');
  const canEditUsers = currentUser?.permissions?.users?.includes('edit');
  const canDeleteUsers = currentUser?.permissions?.users?.includes('delete');

  useEffect(() => {
    fetchUsers();
    fetchRoles();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/users`);
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await axios.get(`${API_URL}/rbac/roles`);
      setRoles(response.data);
    } catch (error) {
      console.error('Failed to load roles');
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      await axios.post(`${API_URL}/users`, newUser);
      toast.success('User created successfully');
      setIsCreateOpen(false);
      setNewUser({ email: '', first_name: '', last_name: '', password: '', role: 'staff' });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    } finally {
      setFormLoading(false);
    }
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    if (!selectedUser) return;
    setFormLoading(true);
    try {
      await axios.patch(`${API_URL}/users/${selectedUser.id}`, editData);
      toast.success('User updated successfully');
      setIsEditOpen(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    const reason = window.prompt(
      'Please enter a reason for deactivating this user (min 5 characters — will be audit-logged):',
      ''
    );
    if (reason === null) return; // user cancelled
    const trimmed = (reason || '').trim();
    if (trimmed.length < 5) {
      toast.error('A reason of at least 5 characters is required.');
      return;
    }
    if (!window.confirm(`Confirm deactivation. Reason: "${trimmed}"`)) return;
    try {
      await axios.delete(`${API_URL}/users/${userId}`, {
        params: { reason: trimmed },
      });
      toast.success('User deactivated successfully');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to deactivate user');
    }
  };

  const handleUnlockSignature = async (u) => {
    if (!window.confirm(`Unlock signature & display name for ${u.first_name} ${u.last_name}? They will be able to re-upload their signature on their next profile visit.`)) return;
    try {
      await axios.post(`${API_URL}/admin/users/${u.id}/unlock-signature`);
      toast.success('Signature unlocked');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unlock signature');
    }
  };

  const openEditDialog = (user) => {
    setSelectedUser(user);
    setEditData({
      first_name: user.first_name,
      last_name: user.last_name,
      role: user.role,
      secondary_roles: user.secondary_roles || [],
      is_active: user.is_active
    });
    setIsEditOpen(true);
  };

  const filteredUsers = users.filter(user => 
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.last_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <CircleNotch size={32} className="animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="users-page">
      {/* Header */}
      <motion.div 
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
      >
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl tracking-tight font-semibold">
            User Management
          </h1>
          <p className="text-muted-foreground text-sm sm:text-base leading-relaxed mt-1">
            Manage users and their roles in your organization
          </p>
        </div>
        {canManageUsers && (
          <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-user-btn">
                <UserPlus size={18} className="mr-2" />
                Add User
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create New User</DialogTitle>
                <DialogDescription>
                  Add a new user to your organization
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateUser} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>First Name</Label>
                    <Input
                      value={newUser.first_name}
                      onChange={(e) => setNewUser(p => ({ ...p, first_name: e.target.value }))}
                      required
                      data-testid="new-user-firstname"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Last Name</Label>
                    <Input
                      value={newUser.last_name}
                      onChange={(e) => setNewUser(p => ({ ...p, last_name: e.target.value }))}
                      required
                      data-testid="new-user-lastname"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={newUser.email}
                    onChange={(e) => setNewUser(p => ({ ...p, email: e.target.value }))}
                    required
                    data-testid="new-user-email"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Password</Label>
                  <PasswordInput
                    value={newUser.password}
                    onChange={(e) => setNewUser(p => ({ ...p, password: e.target.value }))}
                    required
                    minLength={6}
                    data-testid="new-user-password"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Role</Label>
                  <Select 
                    value={newUser.role} 
                    onValueChange={(v) => setNewUser(p => ({ ...p, role: v }))}
                  >
                    <SelectTrigger data-testid="new-user-role">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {roles.filter(r => 
                        currentUser?.role === 'super_admin' || r.id !== 'super_admin'
                      ).map(role => (
                        <SelectItem key={role.id} value={role.id}>
                          {role.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <DialogFooter>
                  <Button type="submit" disabled={formLoading} data-testid="create-user-submit">
                    {formLoading ? <CircleNotch className="animate-spin" size={18} /> : 'Create User'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </motion.div>

      {/* Search */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="relative max-w-md"
      >
        <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
        <Input
          placeholder="Search users..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
          data-testid="user-search"
        />
      </motion.div>

      {/* Users Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="card-tactile rounded-lg overflow-hidden"
      >
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left px-6 py-4 text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">User</th>
                <th className="text-left px-6 py-4 text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">Email</th>
                <th className="text-left px-6 py-4 text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">Role</th>
                <th className="text-left px-6 py-4 text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">Status</th>
                <th className="text-right px-6 py-4 text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filteredUsers.map((user) => (
                <tr key={user.id} className="hover:bg-muted/30 transition-colors" data-testid={`user-row-${user.id}`}>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
                        <span className="text-primary font-medium text-sm">
                          {user.first_name[0]}{user.last_name[0]}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium">{user.first_name} {user.last_name}</p>
                        {user.id === currentUser?.id && (
                          <span className="text-xs text-muted-foreground">(You)</span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-muted-foreground">{user.email}</td>
                  <td className="px-6 py-4">
                    <Badge variant="outline" className={roleColors[user.role]}>
                      {roleLabels[user.role] || user.role}
                    </Badge>
                  </td>
                  <td className="px-6 py-4">
                    {user.is_active ? (
                      <div className="flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400">
                        <ShieldCheck size={16} weight="fill" />
                        <span className="text-sm">Active</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1.5 text-zinc-500">
                        <ShieldWarning size={16} weight="fill" />
                        <span className="text-sm">Inactive</span>
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 text-right">
                    {user.id !== currentUser?.id && (canEditUsers || canDeleteUsers) && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" data-testid={`user-actions-${user.id}`}>
                            <DotsThreeVertical size={18} />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {canEditUsers && (
                            <DropdownMenuItem onClick={() => openEditDialog(user)}>
                              <PencilSimple size={16} className="mr-2" />
                              Edit
                            </DropdownMenuItem>
                          )}
                          {canEditUsers && (
                            <DropdownMenuItem
                              onClick={() => handleUnlockSignature(user)}
                              data-testid={`unlock-signature-${user.id}`}
                            >
                              <Lock size={16} className="mr-2" />
                              Unlock signature
                            </DropdownMenuItem>
                          )}
                          {canDeleteUsers && user.is_active && (
                            <DropdownMenuItem 
                              onClick={() => handleDeleteUser(user.id)}
                              className="text-destructive focus:text-destructive"
                            >
                              <Trash size={16} className="mr-2" />
                              Deactivate
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filteredUsers.length === 0 && (
          <div className="text-center py-12">
            <Users size={48} weight="duotone" className="mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No users found</p>
          </div>
        )}
      </motion.div>

      {/* Edit Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription>
              Update user information and role
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditUser} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>First Name</Label>
                <Input
                  value={editData.first_name}
                  onChange={(e) => setEditData(p => ({ ...p, first_name: e.target.value }))}
                  required
                  data-testid="edit-user-firstname"
                />
              </div>
              <div className="space-y-2">
                <Label>Last Name</Label>
                <Input
                  value={editData.last_name}
                  onChange={(e) => setEditData(p => ({ ...p, last_name: e.target.value }))}
                  required
                  data-testid="edit-user-lastname"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Select 
                value={editData.role} 
                onValueChange={(v) => setEditData(p => ({ ...p, role: v, secondary_roles: (p.secondary_roles || []).filter(r => r !== v) }))}
              >
                <SelectTrigger data-testid="edit-user-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {roles.filter(r => 
                    currentUser?.role === 'super_admin' || r.id !== 'super_admin'
                  ).map(role => (
                    <SelectItem key={role.id} value={role.id}>
                      {role.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Secondary roles (multiple)</Label>
              <p className="text-xs text-muted-foreground">User&apos;s effective permissions = UNION across primary + all secondary roles.</p>
              <div className="grid grid-cols-2 gap-2 border rounded-md p-3 max-h-40 overflow-y-auto">
                {roles.filter(r =>
                  r.id !== editData.role &&
                  (currentUser?.role === 'super_admin' || r.id !== 'super_admin')
                ).map(role => {
                  const checked = (editData.secondary_roles || []).includes(role.id);
                  return (
                    <label key={role.id} className="flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => setEditData(p => {
                          const cur = new Set(p.secondary_roles || []);
                          if (e.target.checked) cur.add(role.id); else cur.delete(role.id);
                          return { ...p, secondary_roles: Array.from(cur) };
                        })}
                        data-testid={`edit-user-secondary-role-${role.id}`}
                      />
                      {role.name}
                    </label>
                  );
                })}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_active"
                checked={editData.is_active}
                onChange={(e) => setEditData(p => ({ ...p, is_active: e.target.checked }))}
                className="rounded"
                data-testid="edit-user-active"
              />
              <Label htmlFor="is_active">Active</Label>
            </div>
            <DialogFooter>
              <Button type="submit" disabled={formLoading} data-testid="edit-user-submit">
                {formLoading ? <CircleNotch className="animate-spin" size={18} /> : 'Save Changes'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
